/**
 * Admin-only nav sections: Admin dashboard, Countries, Companies, Users, Reinsurers,
 * Country Data (yield curves, mortality tables, commission signatures), and Back Office.
 * Rendered at the top of the scrollable nav area for admin roles only.
 */

import { Collapse, List } from '@mui/material';
import {
  AdminPanelSettings as AdminPanelSettingsIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  Flag as FlagIcon,
  GridOn as GridOnIcon,
  MonitorHeart as MonitorHeartIcon,
  People as PeopleIcon,
  Percent as PercentIcon,
  Public as PublicIcon,
  Schedule as ScheduleIcon,
  Shield as ShieldIcon,
  ShowChart as ShowChartIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import { SidebarNavItem, SectionHeader } from '../SidebarNavItem';

interface AdminNavProps {
  sidebarExpanded: boolean;
  isSuperAdmin: boolean;
  isCountryAdmin: boolean;
  isSectionOpen: (label: string) => boolean;
  toggleSection: (label: string) => void;
  onMobileNavClick: () => void;
}

export function AdminNav({
  sidebarExpanded,
  isSuperAdmin,
  isCountryAdmin,
  isSectionOpen,
  toggleSection,
  onMobileNavClick,
}: AdminNavProps) {
  return (
    <>
      {/* Admin section */}
      <SectionHeader
        label="Admin"
        collapsible
        open={isSectionOpen('Admin')}
        onToggle={() => toggleSection('Admin')}
        sidebarExpanded={sidebarExpanded}
      />
      <Collapse in={sidebarExpanded ? isSectionOpen('Admin') : true} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <SidebarNavItem
            to="/admin"
            label="Dashboard"
            icon={<AdminPanelSettingsIcon />}
            sidebarExpanded={sidebarExpanded}
            onNavigate={onMobileNavClick}
          />
          {isSuperAdmin && (
            <SidebarNavItem
              to="/admin/countries"
              label="Countries"
              icon={<PublicIcon />}
              sidebarExpanded={sidebarExpanded}
              onNavigate={onMobileNavClick}
            />
          )}
          <SidebarNavItem
            to="/admin/country-business-classes"
            label="Business Classes"
            icon={<CategoryIcon />}
            sidebarExpanded={sidebarExpanded}
            onNavigate={onMobileNavClick}
          />
          <SidebarNavItem
            to="/admin/companies"
            label="Companies"
            icon={<BusinessIcon />}
            sidebarExpanded={sidebarExpanded}
            onNavigate={onMobileNavClick}
          />
          <SidebarNavItem
            to="/admin/users"
            label="Users"
            icon={<PeopleIcon />}
            sidebarExpanded={sidebarExpanded}
            onNavigate={onMobileNavClick}
          />
          <SidebarNavItem
            to="/admin/reinsurers"
            label="Reinsurers"
            icon={<ShieldIcon />}
            sidebarExpanded={sidebarExpanded}
            onNavigate={onMobileNavClick}
          />
        </List>
      </Collapse>

      {/* Country Data section (country admin only) */}
      {isCountryAdmin && (
        <>
          <SectionHeader
            label="Country Data"
            collapsible
            open={isSectionOpen('Country Data')}
            onToggle={() => toggleSection('Country Data')}
            sidebarExpanded={sidebarExpanded}
          />
          <Collapse in={sidebarExpanded ? isSectionOpen('Country Data') : true} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <SidebarNavItem
                to="/admin/yield-curves"
                label="Yield Curves"
                icon={<ShowChartIcon />}
                sidebarExpanded={sidebarExpanded}
                onNavigate={onMobileNavClick}
              />
              <SidebarNavItem
                to="/admin/mortality-tables"
                label="Mortality Tables"
                icon={<GridOnIcon />}
                sidebarExpanded={sidebarExpanded}
                onNavigate={onMobileNavClick}
              />
              <SidebarNavItem
                to="/admin/commission-signatures"
                label="Commission Signatures"
                icon={<PercentIcon />}
                sidebarExpanded={sidebarExpanded}
                onNavigate={onMobileNavClick}
              />
            </List>
          </Collapse>
        </>
      )}

      {/* Back Office section */}
      <SectionHeader
        label="Back Office"
        collapsible
        open={isSectionOpen('Back Office')}
        onToggle={() => toggleSection('Back Office')}
        sidebarExpanded={sidebarExpanded}
      />
      <Collapse in={sidebarExpanded ? isSectionOpen('Back Office') : true} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <SidebarNavItem
            to="/admin/feature-flags"
            label="Feature Flags"
            icon={<FlagIcon />}
            sidebarExpanded={sidebarExpanded}
            onNavigate={onMobileNavClick}
          />
          <SidebarNavItem
            to="/admin/system-limits"
            label="System Limits"
            icon={<TuneIcon />}
            sidebarExpanded={sidebarExpanded}
            onNavigate={onMobileNavClick}
          />
          <SidebarNavItem
            to="/admin/scheduled-reports"
            label="Scheduled Reports"
            icon={<ScheduleIcon />}
            sidebarExpanded={sidebarExpanded}
            onNavigate={onMobileNavClick}
          />
          <SidebarNavItem
            to="/admin/job-dashboard"
            label="Job Dashboard"
            icon={<MonitorHeartIcon />}
            sidebarExpanded={sidebarExpanded}
            onNavigate={onMobileNavClick}
          />
        </List>
      </Collapse>
    </>
  );
}
